<template>
  <div class="px-6 py-6 space-y-6 bg-zinc-950 text-zinc-400 font-light select-none text-left min-h-screen">
    
    <div class="flex  sm:flex-row justify-between items-start sm:items-center border-b border-zinc-900/60 pb-5 gap-4">
      <div class="space-y-1">
        <h1 class="text-xl font-medium text-white tracking-tight">Outils d'Engagement & Fidélisation</h1>
        <p class="text-xs text-zinc-500 font-light">Ordonnancez vos campagnes de marque, pilotez l'algorithme de fidélité et analysez l'éligibilité des affiliés.</p>
      </div>
      
      <div class="flex gap-3 sm:w-auto">
        <button @click="openLoyaltyModal" class="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 text-xs font-medium uppercase tracking-wider transition-colors">
          Ajuster la Carte
        </button>
        <button @click="openPromoModal" class="flex-1 sm:flex-none bg-white hover:bg-[#D5CCBC] text-black px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 shadow-xl shadow-white/5 active:scale-[0.99]">
          + Créer une Offre
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start w-full">
      
      <div class="space-y-6 lg:col-span-1">
        <div class="bg-zinc-900/20 border border-zinc-900 rounded-2xl p-5 space-y-5">
          <div class="border-b border-zinc-900 pb-3 flex justify-between items-center">
            <h2 class="text-xs font-medium uppercase tracking-widest text-zinc-400 font-mono">Algorithme de Fidélité</h2>
            <span 
              class="px-2 py-0.5 rounded text-[9px] font-mono font-semibold uppercase border"
              :class="loyaltyProgram.actif ? 'bg-emerald-950/20 text-emerald-400 border-emerald-900/40' : 'bg-zinc-900 text-zinc-600 border-zinc-800'"
            >
              {{ loyaltyProgram.actif ? 'Actif' : 'Suspendu' }}
            </span>
          </div>

          <div class="w-full h-44 rounded-2xl bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800/80 p-5 border border-zinc-800/60 shadow-2xl relative flex flex-col justify-between overflow-hidden group">
            <div class="absolute -right-10 -bottom-10 w-32 h-32 bg-white/[0.02] rounded-full blur-2xl group-hover:bg-white/[0.04] transition-all duration-700"></div>
            
            <div class="flex justify-between items-start">
              <div>
                <p class="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">MÉCANISME APPLIQUÉ</p>
                <p class="text-white text-sm font-medium tracking-wide mt-1">{{ loyaltyProgram.nom }}</p>
              </div>
              <span class="text-[#D5CCBC] font-mono text-xs font-light">PRO // SYSTEM</span>
            </div>

            <div class="space-y-1">
              <p class="text-[9px] text-zinc-500 uppercase tracking-wider font-mono">Modalité de déclenchement</p>
              <p class="text-xs text-zinc-300 font-light leading-relaxed">
                Après <span class="text-white font-mono font-medium">{{ loyaltyProgram.palier }}</span> passages honorés, octroi d'une dotation de <span class="text-[#D5CCBC] font-mono font-medium">{{ loyaltyProgram.avantageValue }} {{ loyaltyProgram.avantageType === 'percentage' ? '%' : '€' }}</span> sur l'acte suivant.
              </p>
            </div>
          </div>

          <div class="bg-zinc-950 p-4 border border-zinc-900 rounded-xl space-y-3 text-xs">
            <div class="flex justify-between items-center text-zinc-500">
              <span>Seuil de déclenchement</span>
              <span class="text-zinc-200 font-mono">{{ loyaltyProgram.palier }} visites</span>
            </div>
            <div class="flex justify-between items-center text-zinc-500">
              <span>Type de gratification</span>
              <span class="text-zinc-200 font-mono">{{ loyaltyProgram.avantageType === 'percentage' ? 'Remise proportionnelle' : 'Déduction fixe' }}</span>
            </div>
            <div class="flex justify-between items-center text-zinc-500">
              <span>Affiliés actifs enrôlés</span>
              <span class="text-zinc-200 font-mono">{{ loyaltyProgram.membresInscrits }} profils</span>
            </div>
          </div>

          <div class="flex items-center justify-between p-3 bg-zinc-950 rounded-xl border border-zinc-900">
            <span class="text-[11px] text-zinc-400 font-medium">Accepter les cumuls en ligne</span>
            <input type="checkbox" v-model="loyaltyProgram.cumulEnLigne" class="h-4 w-4 rounded border-zinc-800 bg-zinc-900 text-white accent-zinc-200 cursor-pointer" />
          </div>
        </div>
      </div>

      <div class="space-y-6 lg:col-span-2">
        <div class="bg-zinc-900/20 border border-zinc-900 rounded-2xl p-5 space-y-4">
          <div class="flex justify-between items-center border-b border-zinc-900 pb-3">
            <h3 class="text-sm font-medium text-white tracking-wide">Registre des Offres Tactiques</h3>
            <span class="text-[10px] font-mono uppercase bg-zinc-900 border border-zinc-800 px-2 py-0.5 rounded text-zinc-500">
              {{ promotions.length }} Campagnes répertoriées
            </span>
          </div>

          <div class="overflow-x-auto no-scrollbar">
            <table class="w-full text-xs text-left">
              <thead class="text-zinc-600 border-b border-zinc-900/80 text-[10px] uppercase tracking-wider font-mono">
                <tr>
                  <th class="pb-3 font-medium">Code / Campagne</th>
                  <th class="pb-3 font-medium">Modalité Technique</th>
                  <th class="pb-3 font-medium">Validité Calendaire</th>
                  <th class="pb-3 font-medium">Réseau / État</th>
                  <th class="pb-3 text-right font-medium pr-2">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-zinc-900/40 font-light">
                <tr 
                  v-for="promo in promotions" 
                  :key="promo.id"
                  class="group hover:bg-zinc-900/10 transition-colors"
                >
                  <td class="py-3.5 pr-2 whitespace-nowrap">
                    <span class="text-zinc-200 font-mono font-medium bg-zinc-900 border border-zinc-800 px-2 py-1 rounded text-xs group-hover:text-[#D5CCBC] transition-colors">
                      {{ promo.code }}
                    </span>
                    <span class="block text-[11px] text-zinc-500 font-light mt-1.5">{{ promo.nom }}</span>
                  </td>
                  
                  <td class="py-3.5 pr-2 whitespace-nowrap font-medium text-zinc-300">
                    <span class="text-[#D5CCBC] font-mono font-medium text-sm">-{{ promo.valeur }} {{ promo.type === 'pourcentage' ? '%' : '€' }}</span>
                    <span class="block text-[10px] text-zinc-600 font-light mt-0.5">Sur : {{ promo.cible }}</span>
                  </td>

                  <td class="py-3.5 pr-2 whitespace-nowrap font-mono text-zinc-500 text-[11px]">
                    <div>Du {{ formatDate(promo.debut) }}</div>
                    <div class="text-[10px] text-zinc-600 mt-0.5">Au {{ formatDate(promo.fin) }}</div>
                  </td>

                  <td class="py-3.5 pr-2 whitespace-nowrap">
                    <span 
                      class="px-2 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wider font-mono border"
                      :class="promo.actif ? 'bg-emerald-950/20 text-emerald-400 border-emerald-900/40' : 'bg-zinc-900 text-zinc-600 border-zinc-800'"
                    >
                      {{ promo.actif ? 'Diffusion active' : 'Désactivée' }}
                    </span>
                  </td>

                  <td class="py-3.5 text-right whitespace-nowrap pr-2">
                    <div class="flex items-center justify-end gap-1.5 opacity-40 group-hover:opacity-100 transition-opacity">
                      <button @click="togglePromoStatus(promo)" class="p-1.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-800 rounded-lg transition-colors focus:outline-none" :title="promo.actif ? 'Désactiver' : 'Activer'">
                        <i class="fas" :class="promo.actif ? 'fa-eye-slash' : 'fa-eye'"></i>
                      </button>
                      <button @click="deletePromo(promo.id)" class="p-1.5 bg-zinc-900 hover:bg-red-950/60 text-zinc-600 hover:text-red-400 border border-zinc-800 hover:border-red-900 transition-colors rounded-lg focus:outline-none" title="Purger la campagne">
                        <i class="fas fa-trash text-[10px]"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="promotions.length === 0" class="text-center py-10">
            <p class="text-zinc-600 text-xs italic">Aucun code promotionnel ou offre tactique configuré dans le sommier.</p>
          </div>

        </div>
      </div>

    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import api from '@/api'

// Unified loyalty framework parameters configuration
const loyaltyProgram = reactive({
  nom: "Curation Club Privé",
  palier: 10,
  avantageType: "percentage",
  avantageValue: 15,
  membresInscrits: 0,
  actif: true,
  cumulEnLigne: true
})

// Promotions from backend
const promotions = ref([])

// Fetch loyalty program settings from backend
const fetchLoyaltyProgram = async () => {
  try {
    // TODO: Create proper loyalty program API endpoint
    // For now, count clients enrolled in loyalty
    const clientsResponse = await api.clients.getAllClients()
    const clients = clientsResponse.data || []

    loyaltyProgram.membresInscrits = clients.filter(c => c.loyaltyEnrolled || c.fidelite).length
  } catch (error) {
    console.error('Erreur lors du chargement du programme de fidélité', error)
  }
}

// Fetch promotions from backend
const fetchPromotions = async () => {
  try {
    // TODO: Create proper promotions API endpoint
    // For now, set empty array
    promotions.value = []
  } catch (error) {
    console.error('Erreur lors du chargement des promotions', error)
  }
}

// Load data on mount
onMounted(async () => {
  await fetchLoyaltyProgram()
  await fetchPromotions()
})

// Operational methods dispatch logic
const openLoyaltyModal = () => {
  toast.info('Configuration du programme de fidélité à venir', { position: 'top-right' })
}

const openPromoModal = () => {
  toast.info('Création de promotion à venir', { position: 'top-right' })
}

const togglePromoStatus = async (promo) => {
  try {
    // TODO: Create proper promotion toggle API endpoint
    promo.actif = !promo.actif
    toast.success(`Promotion ${promo.actif ? 'activée' : 'désactivée'}`, { position: 'top-right' })
  } catch (error) {
    console.error('Erreur lors du changement de statut', error)
    toast.error('Erreur lors du changement de statut')
  }
}

const deletePromo = async (id) => {
  try {
    // TODO: Create proper promotion delete API endpoint
    promotions.value = promotions.value.filter(p => p.id !== id)
    toast.success('Promotion supprimée', { position: 'top-right' })
  } catch (error) {
    console.error('Erreur lors de la suppression', error)
    toast.error('Erreur lors de la suppression')
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit' })
}
</script>

<style scoped>
/* Core system scrolling resets */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>